// fetchFaqArticles.js
'use strict';
import parseLinkHeader from 'parse-link-header';

import fetch from '../resource/customFetch.js';
import getMeData from '../selector/getMeData.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import { localeConvertor } from '../resource/i18n.js';

import {
  ADD_LIST_ITEMS,
  SET_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  ADD_INTERCOM_ARTICLES,
} from '../ActionTypes.js';

/**
 * Fetch faq articles
 * @kind action
 * @param {number} [ page = 1 ] - page number.
 * @param {number} [ limit = 10 ] - page size.
 * @param {string} {language} - data content language.
 * @param {string} {collection} -  data collection
 * @param {object} { [httpProxyHeaders = {}] } - http proxy headers for SSR.
 * @return {Promise} Action promise.
 */
const fetchFaqArticles =
  ({
    page = 1,
    limit = 10,
    language = 'en',
    collection = 'user',
    httpProxyHeaders = {},
  }) =>
  async (dispatch, getState) => {
    const listSelectPath = ['faq', language];
    const selectPath = [...listSelectPath, page];
    const token = getMeData(getState(), 'token');

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        ...httpProxyHeaders,
      },
    };

    if (token) {
      fetchOptions.headers.Authorization = `Bearer ${token}`;
    }

    const fetchUrl = getResourceUrl({ endpoint: '/intercom/articles' });
    fetchUrl.searchParams.append('limit', limit);
    fetchUrl.searchParams.append('page', page);
    fetchUrl.searchParams.append('collection', collection);
    fetchUrl.searchParams.append(
      'lang',
      localeConvertor({
        locale: language,
        isISO639: true,
      })
    );
    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });
    try {
      const response = await fetch(fetchUrl.href, fetchOptions);

      if (!response.ok) {
        await handleFetchError({ response });
      }

      const payload = await response.json();
      const isFirstPage = page === 1;
      const links = parseLinkHeader(response.headers.get('Link'));
      const nextPage = links && links.next && parseInt(links.next.page, 10);
      const lastPage = links && links.last && parseInt(links.last.page, 10);
      const totalCount = parseInt(response.headers.get('x-total-count'), 10);
      const itemIds = payload.map(item => item.id);
      const articles = payload.reduce((current, item) => {
        current[item.id] = {
          [language]: { ...item },
        };
        return current;
      }, {});
      dispatch({
        type: ADD_INTERCOM_ARTICLES,
        payload: { articles },
      });
      dispatch({
        type: isFirstPage ? SET_LIST_ITEMS : ADD_LIST_ITEMS,
        payload: {
          selectPath: listSelectPath,
          itemIds,
          totalCount,
          nextPage,
          lastPage,
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default fetchFaqArticles;
